import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ANONYMOUS_TOKEN_EXPIRED,
  AnonymousTokenService,
  AUTH_ERROR_HEADER,
} from '@core/authen/anonymous-token.service';
import { AuthenService, REFRESH_TOKEN_PATH } from '@core/authen/authen.service';
import { SessionService } from '@core/session.service';
import { HttpStatusCodeEnum } from '@shared/enums/httpstatuscode.enum';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UserInterface } from '@shared/interfaces/user.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

const ANONYMOUS_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthorizationInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<UserInterface>(
    null
  );

  constructor(
    private readonly sessionService: SessionService,
    private readonly authService: AuthenService,
    private readonly anonymousTokenService: AnonymousTokenService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpResponse<HttpErrorResponse>) => {
        if (error.status === HttpStatusCodeEnum.Unauthorized) {
          if (
            this.isAnonymousTokenExpired(error) &&
            !request.context.get(ANONYMOUS_RETRIED)
          ) {
            return this.handleAnonymousTokenExpired(next, request);
          }

          return this.handle401Error(error, next, request);
        }

        throw error;
      })
    );
  }

  private isAnonymousTokenExpired(
    error: HttpResponse<HttpErrorResponse>
  ): boolean {
    return error?.headers?.get(AUTH_ERROR_HEADER) === ANONYMOUS_TOKEN_EXPIRED;
  }

  private handleAnonymousTokenExpired(
    next: HttpHandler,
    request: HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    const retriedRequest = request.clone({
      context: request.context.set(ANONYMOUS_RETRIED, true),
    });

    return this.anonymousTokenService
      .refreshToken()
      .pipe(switchMap(() => next.handle(retriedRequest)));
  }

  private handle401Error(
    error: HttpEvent<unknown>,
    next: HttpHandler,
    request: HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    if (request.url.includes(REFRESH_TOKEN_PATH)) {
      this.handleRefreshtokenExpired();
      throw error;
    }

    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(Boolean),
        take(1),
        switchMap(() => next.handle(request))
      );
    } else {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.authService.getRefreshToken();

      if (refreshToken) {
        return this.authService.refreshToken().pipe(
          switchMap((data: UserInterface) => {
            if (data) {
              this.authService.handleLoginSuccess(data);
              this.refreshTokenSubject.next(data);

              return next.handle(request);
            }
            this.handleRefreshtokenExpired();

            return next.handle(request);
          }),
          catchError(() => {
            this.handleRefreshtokenExpired();

            return next.handle(request);
          }),
          finalize(() => (this.isRefreshing = false))
        );
      }

      throw error;
    }
  }

  handleRefreshtokenExpired(): void {
    this.sessionService.setLoginCallBackpage(location.href);
    this.authService.handleLoginFailed();

    this.sessionService.setCookie(StorageEnum.isTokenExpired, true);
  }
}
